import React from "react";
import styles from "./details-form.module.scss";
import Dropdown from "../../components/ui-kit/dropdown/dropdown";
import Input from "../../components/ui-kit/Input/input";
import NoteInput from "../../components/ui-kit/note-input/note-input";
import { FormDataEditDetails } from "../../interfaces/krookie";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks/useAppSelector";

interface DetailsFormProps {
  formData: FormDataEditDetails | null;
  onFormDataChange: (data: FormDataEditDetails) => void;
  disabled?: Partial<Record<keyof FormDataEditDetails, boolean>>;
}

const DetailsForm: FC<DetailsFormProps> = ({
  formData,
  onFormDataChange,
  disabled,
}) => {
  const { t } = useTranslation();

  const { cities, landUse, phase } = useAppSelector(
    (state) => state.masterPlanModification,
  );

  const handleInputChange =
    (field: keyof FormDataEditDetails) =>
    (value: string | string[] | number | number[] | null) => {
      if (!formData) return;

      if (field === "buildArea" && typeof value === "string") {
        if (!/^\d*\.?\d*$/.test(value)) return;
        const decimalPart = value.split(".")[1];
        if (decimalPart && decimalPart.length > 2) {
          return;
        }
        let numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
          numericValue = 0;
        }
        if (numericValue > 100) {
          return;
        }
      }
      if (field === "height" && typeof value === "string") {
        if (!/^\d*\.?\d*$/.test(value)) return;
        const [integerPart, decimalPart] = value.split(".");
        if (integerPart.length > 4) {
          return;
        }
        if (decimalPart && decimalPart.length > 2) {
          return;
        }
        let numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
          numericValue = 0;
        }
        if (numericValue > 9999.99) {
          return;
        }
      }
      if (
        ["front", "side", "back"].includes(field) &&
        typeof value === "string"
      ) {
        if (!/^\d*\.?\d*$/.test(value)) return;
        const [integerPart, decimalPart] = value.split(".");
        if (integerPart.length > 2) {
          return;
        }
        if (decimalPart && decimalPart.length > 1) {
          return;
        }
        let numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
          numericValue = 0;
        }
        if (numericValue > 99.9) {
          return;
        }
      }
      if (field === "cityEn" || field === "phase" || field === "landUse") {
        const selectedValue = value as number;
        let displayValue = "";
        if (field === "cityEn") {
          displayValue = cities.get(selectedValue) || "";
        }
        if (field === "phase") {
          displayValue = phase.get(selectedValue) || "";
        }
        if (field === "landUse") {
          displayValue = landUse.get(selectedValue) || "";
        }
        onFormDataChange({
          ...formData,
          [field]: { displayValue, value: selectedValue },
        });
      } else {
        onFormDataChange({
          ...formData,
          [field]: value,
        });
      }
    };

  const handleNotesChange = (notes: string[]) => {
    if (!formData) return;
    onFormDataChange({
      ...formData,
      notes,
    });
  };

  return (
    <div className={styles.form}>
      <div className={styles.row}>
        <div className={styles.inputGroup}>
          <Dropdown
            value={formData?.cityEn.value ?? undefined}
            disabled={disabled?.cityEn}
            title={t("editDetailsBlock.industrialCity")}
            options={cities}
            classes={{ label: `${styles.customDropdownLabel}` }}
            onChange={handleInputChange("cityEn")}
            onClear={() => handleInputChange("cityEn")("")}
          />
        </div>
        <div className={styles.inputGroup}>
          <Dropdown
            value={formData?.phase.value ?? undefined}
            onChange={handleInputChange("phase")}
            disabled={disabled?.phase}
            title={t("editDetailsBlock.phase")}
            options={phase}
            classes={{ label: `${styles.customDropdownLabel}` }}
            onClear={() => handleInputChange("phase")("")}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.inputGroup}>
          <Input
            classes={{ label: `${styles.customInputTitle}` }}
            label={t("editDetailsBlock.plotNumber")}
            value={formData?.plotNumber}
            disabled={disabled?.plotNumber}
            onChange={handleInputChange("plotNumber")}
            maxLength={100}
          />
        </div>
        <div className={styles.inputGroup}>
          <Input
            classes={{ label: `${styles.customInputTitle}` }}
            label={t("editDetailsBlock.oldPlotNumber")}
            value={formData?.oldPlotNumber}
            onChange={handleInputChange("oldPlotNumber")}
            disabled={disabled?.oldPlotNumber}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.inputGroup}>
          <Input
            classes={{ label: `${styles.customInputTitle}` }}
            label={t("editDetailsBlock.streetNumber")}
            value={formData?.streetNumber}
            onChange={handleInputChange("streetNumber")}
            maxLength={200}
            disabled={disabled?.streetNumber}
          />
        </div>
        <div className={styles.inputGroup}>
          <Dropdown
            onChange={handleInputChange("landUse")}
            title={t("editDetailsBlock.landUse")}
            value={formData?.landUse.value ?? undefined}
            classes={{ label: `${styles.customDropdownLabel}` }}
            options={landUse}
            onClear={() => handleInputChange("landUse")("")}
            disabled={disabled?.landUse}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.inputGroup}>
          <Input
            classes={{ label: `${styles.customInputTitle}` }}
            label={t("editDetailsBlock.buildArea")}
            value={formData?.buildArea}
            onChange={handleInputChange("buildArea")}
            disabled={disabled?.buildArea}
          />
        </div>
        <div className={styles.inputGroup}>
          <Input
            classes={{ label: `${styles.customInputTitle}` }}
            label={t("editDetailsBlock.area")}
            value={formData?.area}
            onChange={handleInputChange("area")}
            disabled={disabled?.area}
          />
        </div>
      </div>

      <div className={styles.measurementGroup}>
        <Input
          value={formData?.height}
          classes={{ label: `${styles.customInputTitle}` }}
          onChange={handleInputChange("height")}
          label={t("editDetailsBlock.maxHeight")}
          disabled={disabled?.height}
        />
        <div className={styles.setBackValues}>
          <Input
            value={formData?.front}
            classes={{
              label: `${styles.customInputTitle}`,
              container: `${styles.inputSetBack}`,
            }}
            onChange={handleInputChange("front")}
            label={t("editDetailsBlock.front")}
            disabled={disabled?.front}
          />
          <Input
            value={formData?.side}
            classes={{
              label: `${styles.customInputTitle}`,
              container: `${styles.inputSetBack}`,
            }}
            onChange={handleInputChange("side")}
            label={t("editDetailsBlock.side")}
            disabled={disabled?.side}
          />
          <Input
            value={formData?.back}
            classes={{
              label: `${styles.customInputTitle}`,
              container: `${styles.inputSetBack}`,
            }}
            onChange={handleInputChange("back")}
            label={t("editDetailsBlock.back")}
            disabled={disabled?.back}
          />
        </div>
      </div>
      <Input
        value={formData?.previousKrookieCode}
        classes={{ label: `${styles.customInputTitle}` }}
        onChange={handleInputChange("previousKrookieCode")}
        label={t("editDetailsBlock.previousKrookieCode")}
        disabled={disabled?.previousKrookieCode}
      />
      <div className={styles.notes}>
        <NoteInput
          initialNotes={formData?.notes}
          onNotesChange={handleNotesChange}
        />
      </div>
    </div>
  );
};

export default DetailsForm;
